import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { 
  ChevronLeft, 
  ChevronRight,
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  BarChart3,
  Settings,
  ChevronDown,
  UserCircle,
  UserPlus,
  Clock,
  Route,
  Receipt,
  TrendingUp,
  Shield,
  Building,
  CreditCard
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/components/ui/collapsible';
import { useBranding } from '@/shared/contexts/BrandingContext';
import { useAuth } from '@/shared/hooks/useAuth';

interface NavigationGroup {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavigationItem[];
}

interface NavigationItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

const navigationGroups: NavigationGroup[] = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { title: 'Overview', href: '/admin/command-center', icon: LayoutDashboard },
      { title: 'Analytics', href: '/analytics-dashboard', icon: TrendingUp },
    ]
  },
  {
    title: 'Customers',
    icon: Users,
    items: [
      { title: 'Customer List', href: '/customer-management', icon: Users },
      { title: 'Leads', href: '/customer-management?tab=leads', icon: UserPlus },
      { title: 'Contacts', href: '/customer-management?tab=contacts', icon: UserCircle },
    ]
  },
  {
    title: 'Scheduling',
    icon: Calendar,
    items: [
      { title: 'Calendar', href: '/predictive-calendar', icon: Calendar },
      { title: 'Appointments', href: '/customer-management?tab=appointments', icon: Clock },
      { title: 'Routes', href: '/field-technician', icon: Route },
    ]
  },
  {
    title: 'Quotes & Invoices',
    icon: FileText,
    items: [
      { title: 'Quote Manager', href: '/quotation', icon: FileText },
      { title: 'Invoices', href: '/customer-management?tab=invoices', icon: Receipt },
      { title: 'Pricing Admin', href: '/pricing-admin', icon: CreditCard },
    ]
  },
  {
    title: 'Reporting',
    icon: BarChart3,
    items: [
      { title: 'Treatment Reports', href: '/treatment-reports', icon: FileText },
      { title: 'Analytics', href: '/analytics-dashboard', icon: BarChart3 },
      { title: 'Chemical Safety Data Sheets', href: '/chemical-safety-data-sheets', icon: Shield },
      { title: 'Compliance', href: '/compliance-audit', icon: Shield },
    ]
  },
  {
    title: 'Settings',
    icon: Settings,
    items: [
      { title: 'Company Profile', href: '/profile', icon: Building },
      { title: 'User Management', href: '/access-control', icon: Users },
      { title: 'Billing', href: '/pricing', icon: CreditCard },
      { title: 'System Settings', href: '/workflow-admin', icon: Settings },
    ]
  }
];

export default function SidebarNav() {
  const [location] = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<string[]>(['Dashboard', 'Customers']);

  // Apply margin to sibling content when sidebar is visible
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.style.marginLeft = isCollapsed ? '64px' : '256px';
    }
    
    return () => {
      if (mainElement) {
        mainElement.style.marginLeft = '0px';
      }
    };
  }, [isCollapsed]);

  const toggleGroup = (groupTitle: string) => {
    setOpenGroups(prev =>
      prev.includes(groupTitle)
        ? prev.filter(g => g !== groupTitle)
        : [...prev, groupTitle]
    );
  };

  const isActiveLink = (href: string) => {
    if (href === '/admin/command-center' && location === '/') return true;
    
    // Handle query strings properly - compare base paths
    const basePath = href.split('?')[0];
    return location === basePath || location.startsWith(basePath + '/');
  };

  return (
    <div 
      className={`fixed left-0 top-0 z-40 h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
      style={{ marginTop: '0px' }} // Adjust based on header height
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        {!isCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">A360</span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">Aegis360</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="h-8 w-8"
          data-testid="sidebar-toggle"
        >
          {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation Content */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-2">
          {navigationGroups.map((group) => {
            const isGroupOpen = openGroups.includes(group.title);
            
            return (
              <Collapsible
                key={group.title}
                open={isGroupOpen}
                onOpenChange={() => toggleGroup(group.title)}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start h-10 px-3 ${
                      isCollapsed ? 'px-2' : ''
                    }`}
                    data-testid={`nav-group-${group.title.toLowerCase().replace(' ', '-')}`}
                  >
                    <group.icon className={`h-4 w-4 ${isCollapsed ? '' : 'mr-3'}`} />
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-left font-medium">{group.title}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${
                          isGroupOpen ? 'transform rotate-180' : ''
                        }`} />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
                
                {!isCollapsed && (
                  <CollapsibleContent className="space-y-1 ml-4 mt-1">
                    {group.items.map((item) => (
                      <Link key={item.href} href={item.href}>
                        <Button
                          variant="ghost"
                          className={`w-full justify-start h-9 px-3 text-sm ${
                            isActiveLink(item.href)
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                              : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                          }`}
                          data-testid={`nav-item-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <item.icon className="h-4 w-4 mr-3" />
                          <span className="flex-1 text-left">{item.title}</span>
                          {item.badge && (
                            <span className="ml-auto text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              {item.badge}
                            </span>
                          )}
                        </Button>
                      </Link>
                    ))}
                  </CollapsibleContent>
                )}
              </Collapsible>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer */}
      {!isCollapsed && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Aegis360 Dashboard
          </div>
        </div>
      )}
    </div>
  );
}